import "i18next";
import { useTranslation } from "react-i18next";
import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import SnackBar from "components/SnackBar/SnackBar";
import BackdropComponent from "components/Spinner/Spinner";
import {
  Autocomplete,
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { styled } from "@mui/material/styles";
import {
  getRequisitionList,
  getRequisitionDetails,
  getRosterDetailsByRequisitionId,
  RemoveRosterRequisitionMap,
} from "services/userServices";
import { GridSearchIcon } from "@mui/x-data-grid";
import { debounce } from "lodash";
import { format } from "date-fns";
import { OpenInNew } from "@mui/icons-material";
import PrimeReactDataGrid from "components/PrimeReactDataGrid/PrimeReactDataGrid";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import DialogBox from "components/ConfirmationDialog/DialogBox";

const SubHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "Urbanist, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  fontWeight: "bold",
  paddingBottom: theme.spacing(0.75),
}));
export default function SearchRequisitions({ setRosterLength }) {
  const { t } = useTranslation(["SearchRequisitions"], {
    useSuspense: false,
  });
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const snackReset = () => {
    setSnack({
      open: false,
      message: t("alert."),
      severity: "",
    });
  };
  const [requisitionList, setRequisitionList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRequisitionId, setSelectedRequisitionId] = useState("");
  const [requisitionDetails, setRequisitionDetails] = useState([]);
  const [rosterDetails, setRosterDetails] = useState([]);
  const inputRef = useRef(null);
  const autoCompleteRef = useRef(null);
  const justSelectedRef = useRef(false);
  const [autoCompleteOpen, setAutoCompleteOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRoster, setSelectedRoster] = useState(null);

  const requisitionDetailsColumns = [
    {
      field: "classRosterId",
      header: t("column_header.remove"),
      align: "center",
      bodyStyle: {
        textAlign: "center",
      },
      body: (rowData) => (
        <Tooltip
          title={
            <Typography variant="body2" sx={{ fontSize: "1rem" }}>
              {t("title.remove_roster")}
            </Typography>
          }
          arrow
        >
          <IconButton
            onClick={() => {
              setSelectedRoster(rowData.classRosterId);
              setDialogOpen(true);
            }}
          >
            <LinkOffIcon
              sx={{
                color: "#3299FF",
                fontSize: "1.5rem",
              }}
            />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "classRosterId",
      header: t("column_header.roster_id"),
      sortable: true,
      align: "center",
      bodyStyle: {
        textAlign: "center",
      },
      body: (rowData) => (
        <Tooltip
          title={
            <Typography variant="body2" sx={{ fontSize: "1rem" }}>
              {t("title.view_roster")}
            </Typography>
          }
          arrow
        >
          <IconButton
            sx={{
              color: "#3299FF",
              "&:hover": {
                color: "#2878CC",
              },
            }}
            onClick={() => {
              const url = `/home/searchrosters?classRequestId=${rowData.classRequestId}&classRosterId=${rowData.classRosterId}`;
              window.open(url, "_blank"); // Open in a new tab with query parameters
            }}
          >
            {rowData.classRosterId}
            <OpenInNew
              sx={{
                ml: 0.5,
              }}
              fontSize="small"
            />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "rosterStatus",
      header: t("column_header.roster_status"),
      sortable: true,
      filter: true,
      filterPlaceholder: t("filter_placeholder.search_by_roster_status"),
      align: "center",
      bodyStyle: {
        textAlign: "left",
      },
    },
    {
      field: "classRequestId",
      header: t("column_header.request_id"),
      sortable: true,
      align: "center",
      bodyStyle: {
        textAlign: "right",
      },
    },
    {
      field: "requestStatus",
      header: t("column_header.request_status"),
      sortable: true,
      filter: true,
      filterPlaceholder: t("filter_placeholder.search_by_status"),
      align: "center",
      bodyStyle: {
        textAlign: "left",
      },
    },
    {
      field: "wave",
      header: t("column_header.wave"),
      sortable: true,
      align: "center",
      bodyStyle: {
        textAlign: "right",
      },
    },
    {
      field: "techEquipmentSolution",
      header: t("column_header.tech_equipment_solution"),
      sortable: true,
      filter: true,
      filterPlaceholder: t(
        "filter_placeholder.search_by_tech_equipment_solution",
      ),
      align: "center",
      bodyStyle: {
        textAlign: "left",
      },
    },
    {
      field: "parentClientName",
      header: t("column_header.parent_client"),
      sortable: true,
      filter: true,
      filterPlaceholder: t("filter_placeholder.search_by_parent_client"),
      align: "center",
      bodyStyle: {
        textAlign: "left",
      },
    },
    {
      field: "trainerName",
      header: t("column_header.trainer_name"),
      sortable: true,
      filter: true,
      filterPlaceholder: t("filter_placeholder.search_by_trainer"),
      align: "center",
      bodyStyle: {
        textAlign: "left",
      },
      body: (rowData) =>
        rowData.trainerName
          ? `${rowData.trainerName} (${rowData.trainerId || "NA"})`
          : "NA",
    },
    {
      field: "ogName",
      header: t("column_header.og_name"),
      sortable: true,
      filter: true,
      filterPlaceholder: t("filter_placeholder.search_by_og"),
      align: "center",
      bodyStyle: {
        textAlign: "left",
      },
      body: (rowData) =>
        rowData.ogName ? `${rowData.ogName} (${rowData.ogId || "NA"})` : "NA",
    },
  ];
  const filterProps = {
    global: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    requestStatus: {
      operator: FilterOperator.AND,
      constraints: [
        {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
      ],
      filterPlaceholder: t("filter_placeholder.search_by_request_status"),
    },
    rosterStatus: {
      operator: FilterOperator.AND,
      constraints: [
        {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
      ],
      filterPlaceholder: t("filter_placeholder.search_by_roster_status"),
    },
    techEquipmentSolution: {
      operator: FilterOperator.AND,
      constraints: [
        {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
      ],
      filterPlaceholder: t(
        "filter_placeholder.search_by_tech_equipment_solution",
      ),
    },
    trainerName: {
      operator: FilterOperator.AND,
      constraints: [
        {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
      ],
      filterPlaceholder: t("filter_placeholder.search_by_trainer_name"),
    },
    ogName: {
      operator: FilterOperator.AND,
      constraints: [
        {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
      ],
      filterPlaceholder: t("filter_placeholder.search_by_og_name"),
    },
    parentClientName: {
      operator: FilterOperator.AND,
      constraints: [
        {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
      ],
      filterPlaceholder: t("filter_placeholder.search_by_parent_client"),
    },
  };

  const handleRemoveRoster = async () => {
    if (selectedRoster && selectedRequisitionId) {
      try {
        setDialogOpen(false);
        setLoading(true);
        const deleteRequisitionPayload = {
          classRosterId: selectedRoster,
          requisitionId: selectedRequisitionId,
          modifiedBy: localStorage.getItem("User"),
        };
        const result = await RemoveRosterRequisitionMap(
          deleteRequisitionPayload,
        );
        if (result?.message === "Success") {
          setRequisitionList((prevList) =>
            prevList.map((item) => {
              if (item.requisitionId === selectedRequisitionId) {
                return {
                  ...item,
                  totalRoster: Math.max((item.totalRoster || 1) - 1, 0),
                };
              }
              return item;
            }),
          );
          setRosterDetails((prev) =>
            prev.filter((roster) => roster.classRosterId !== selectedRoster),
          );
          setRosterLength((prev) => prev - 1);
          setSnack({
            open: true,
            message: t("alert.requisition_removed", { selectedRoster }),
            severity: "success",
          });
        } else {
          setSnack({
            open: true,
            message: t("alert.requisition_remove_failed"),
            severity: "error",
          });
        }
      } catch {
        setSnack({
          open: true,
          message: t("error_requisition_cancelling_failed"),
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!selectedRequisitionId) return;

    let cancelled = false;

    const fetchRequisitionAndRosterDetails = async () => {
      if (!cancelled) {
        setLoading(true);
      }

      try {
        // 🔹 Requisition Details
        const detailsResponse = await getRequisitionDetails(
          selectedRequisitionId,
        );

        if (cancelled) return;

        const details = detailsResponse?.data || [];

        if (!cancelled) {
          setRequisitionDetails(details);
          setRosterLength(
            requisitionList.filter(
              (req) => req.requisitionId === selectedRequisitionId,
            )[0]?.totalRoster || 0,
          );

          if (!details.length) {
            setSnack({
              open: true,
              message: t("alert.no_requisition_details_found"),
              severity: "info",
            });
          }
        }
      } catch (error) {
        if (!cancelled) {
          setRequisitionDetails([]);
          setRosterLength(0);
          setSnack({
            open: true,
            message: t("alert.error_fetching_requisition_details"),
            severity: "error",
          });
        }
      }

      if (cancelled) return;

      try {
        // 🔹 Roster Details
        const rosterResponse = await getRosterDetailsByRequisitionId(
          selectedRequisitionId,
        );

        if (cancelled) return;

        const rosters = rosterResponse?.data || [];

        if (!cancelled) {
          setRosterDetails(rosters);

          if (!rosters.length) {
            setSnack({
              open: true,
              message: t("alert.no_rosters_found_for_requisition"),
              severity: "info",
            });
          }
        }
      } catch (error) {
        if (!cancelled) {
          setRosterDetails([]);
          setSnack({
            open: true,
            message: t("alert.error_fetching_roster_details"),
            severity: "error",
          });
        }
      }

      if (!cancelled) {
        setLoading(false);
      }
    };

    fetchRequisitionAndRosterDetails();

    return () => {
      cancelled = true;
    };
  }, [selectedRequisitionId, setRosterLength]);

  const fetchRequisitionList = async (query = "") => {
    if (!query) {
      setRequisitionList([]);
      setRequisitionDetails([]);
      setRosterDetails([]);
      setRosterLength(0);
      return;
    }
    try {
      setLoading(true);
      const response = await getRequisitionList();
      if (response?.message === "Success") {
        const filteredData = response.data
          .filter((item) =>
            item.requisitionId.toUpperCase().includes(query.toUpperCase()),
          )
          .slice(0, 10);

        if (
          query.length === 5 &&
          filteredData.length === 1 &&
          filteredData[0].requisitionId.toUpperCase() === query.toUpperCase()
        ) {
          setSelectedRequisitionId(filteredData[0].requisitionId);
          setSearchQuery(filteredData[0].requisitionId);
          setRequisitionList(filteredData);
        } else if (filteredData.length > 0) {
          setRequisitionList(filteredData);
        } else {
          setRequisitionList([]);
          setRequisitionDetails([]);
          setRosterDetails([]);
          setRosterLength(0);
          setSnack({
            open: true,
            message: t("error_no_requisition_found", { query }),
            severity: "error",
          });
        }
      } else {
        setRequisitionList([]);
        setRequisitionDetails([]);
        setRosterDetails([]);
        setRosterLength(0);
        setSnack({
          open: true,
          message: t("alert.failed_to_fetch_requisition_list_"),
          severity: "error",
        });
      }
    } catch {
      setSnack({
        open: true,
        message: t("alert.error_fetching_requisition_list_details_"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  const debouncedFetch = debounce((query) => fetchRequisitionList(query), 500);
  useEffect(() => {
    if (!loading && requisitionList.length > 0 && inputRef.current) {
      if (!justSelectedRef.current) {
        inputRef.current.focus();
        setAutoCompleteOpen(true);
      } else {
        // Reset the flag immediately after use
        justSelectedRef.current = false;
      }
    }
  }, [loading, requisitionList]);
  return (
    <>
      {loading ? <BackdropComponent /> : null}

      <DialogBox
        isOpen={dialogOpen}
        action={handleRemoveRoster}
        cancelAction={() => setDialogOpen(false)}
        title={t("dialog.remove_roster_title")}
        message={t("dialog.remove_roster_message")}
        itemList={
          selectedRoster && selectedRequisitionId
            ? [
                t("dialog.item_requisition_id", {
                  requisitionId: selectedRequisitionId,
                }),
                t("dialog.item_roster_id", {
                  rosterId: selectedRoster,
                }),
              ]
            : []
        }
        messageType="Confirmation"
        isConfirmation={true}
      />

      <Grid container spacing={2}>
        <Grid item xs={3}>
          <SubHeading>{t("subheading.search_requisition_by_id")}</SubHeading>
          <Autocomplete
            freeSolo
            disabled={loading}
            ref={autoCompleteRef}
            options={requisitionList}
            open={autoCompleteOpen}
            onOpen={() => setAutoCompleteOpen(true)}
            onClose={() => setAutoCompleteOpen(false)}
            getOptionLabel={(option) => option.requisitionId || ""}
            value={
              requisitionList.find(
                (opt) => opt.requisitionId === searchQuery,
              ) || null
            }
            onInputChange={(event, newValue) => {
              if (!event) return;
              let sanitizedValue = (newValue || "")
                .toUpperCase()
                .replace(/[^A-Z0-9]/g, "")
                .slice(0, 5);
              setSearchQuery(sanitizedValue);
              if (sanitizedValue.length >= 3) {
                debouncedFetch(sanitizedValue);
              } else {
                setRequisitionList([]);
                setRequisitionDetails([]);
                setRosterDetails([]);
                setRosterLength(0);
                setAutoCompleteOpen(false);
              }
            }}
            onChange={(event, newValue) => {
              if (newValue) {
                setSelectedRequisitionId(newValue.requisitionId);
                setSearchQuery(newValue.requisitionId);
              } else {
                setSelectedRequisitionId("");
                setSearchQuery("");
              }
              justSelectedRef.current = true;
              setAutoCompleteOpen(false);
            }}
            onBlur={() => {
              if (
                searchQuery.length === 5 &&
                requisitionList.length === 1 &&
                requisitionList[0].requisitionId === searchQuery
              ) {
                setSelectedRequisitionId(requisitionList[0].requisitionId);
              }
            }}
            renderOption={(props, option) => (
              <ListItem
                {...props}
                key={option.requisitionId}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  pr: 1,
                }}
              >
                <ListItemText primary={option.requisitionId} />

                {option.totalRoster > 0 && (
                  <Tooltip
                    title={
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "1rem",
                        }}
                      >
                        {option.isAdhocRequisition
                          ? t("tooltip.adhoc_requisition")
                          : t("tooltip.linked_requisition")}
                      </Typography>
                    }
                    arrow
                  >
                    <Chip
                      label={option.totalRoster}
                      size="small"
                      sx={{
                        backgroundColor: option.isAdhocRequisition
                          ? "#FFA000"
                          : "#3299FF",
                        color: "white",
                        fontWeight: 500,
                        fontSize: "0.75rem",
                      }}
                    />
                  </Tooltip>
                )}
              </ListItem>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={inputRef}
                disabled={loading}
                placeholder={t("placeholder.enter_requisition_id_min_3_chars_")}
                variant="outlined"
                size="small"
                inputProps={{
                  ...params.inputProps,
                  maxLength: 5,
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    let inputValue = (event.target.value || "")
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, "")
                      .slice(0, 5);
                    if (inputValue.length >= 3) {
                      fetchRequisitionList(inputValue);
                      setSearchQuery(inputValue);
                    }
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={
                          <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                            {t("title.search")}
                          </Typography>
                        }
                      >
                        <IconButton
                          onClick={() => {
                            if ((searchQuery || "").length >= 3)
                              fetchRequisitionList(searchQuery);
                          }}
                          disabled={(searchQuery || "").length < 3}
                        >
                          <GridSearchIcon />
                        </IconButton>
                      </Tooltip>
                      {params.InputProps.endAdornment}
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={9}>
          &nbsp;
        </Grid>
        {requisitionDetails.length > 0 && (
          <>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={4}>
              <SubHeading>{t("subheading.title")}</SubHeading>
              <Typography variant="body1">
                {requisitionDetails[0].requisitionTitle}
              </Typography>
            </Grid>

            <Grid item xs={1}>
              <SubHeading>{t("subheading.status")}</SubHeading>
              <Typography variant="body1">
                {requisitionDetails[0].requisitionStatus}
              </Typography>
            </Grid>

            <Grid item xs={1}>
              <SubHeading>{t("subheading.wave")}</SubHeading>
              <Typography variant="body1">
                {requisitionDetails[0].wave || "NA"}
              </Typography>
            </Grid>

            <Grid item xs={1.5}>
              <SubHeading>{t("subheading.number_of_openings")}</SubHeading>
              <Typography variant="body1">
                {requisitionDetails[0].numberOfOpenings}
              </Typography>
            </Grid>

            <Grid item xs={1.5}>
              <SubHeading>{t("subheading.class_fill_goal")}</SubHeading>
              <Typography variant="body1">
                {requisitionDetails[0].classFillGoal || "0"}
              </Typography>
            </Grid>

            <Grid item xs={1.5}>
              <SubHeading>{t("subheading.production_fill_goal")}</SubHeading>
              <Typography variant="body1">
                {requisitionDetails[0].productionFillGoal || "0"}
              </Typography>
            </Grid>

            <Grid item xs={1}>
              <SubHeading>{t("subheading.justification")}</SubHeading>
              <Typography variant="body1">
                {requisitionDetails[0].justification || "NA"}
              </Typography>
            </Grid>

            <Grid item xs={2}>
              <SubHeading>{t("subheading.start_date")}</SubHeading>
              <Typography variant="body1">
                {requisitionDetails[0].classStartDate
                  ? format(
                      new Date(requisitionDetails[0].classStartDate),
                      "dd-MMM-yyyy",
                    )
                  : t("typography.na")}
              </Typography>
            </Grid>

            <Grid item xs={2}>
              <SubHeading>{t("subheading.roster_lock_date")}</SubHeading>
              <Typography variant="body1">
                {requisitionDetails[0].rosterLockDate
                  ? format(
                      new Date(requisitionDetails[0].rosterLockDate),
                      "dd-MMM-yyyy",
                    )
                  : t("typography.na")}
              </Typography>
            </Grid>

            <Grid item xs={2}>
              <SubHeading>{t("subheading.trainer")}</SubHeading>
              <Typography variant="body1">
                {requisitionDetails[0].trainerName
                  ? `${requisitionDetails[0].trainerName} (${
                      requisitionDetails[0].trainerId || "NA"
                    })`
                  : t("typography.na")}
              </Typography>
            </Grid>

            <Grid item xs={2}>
              <SubHeading>{t("subheading.recruiter")}</SubHeading>
              <Typography variant="body1">
                {requisitionDetails[0].recruiterName
                  ? `${requisitionDetails[0].recruiterName} (${
                      requisitionDetails[0].recruiterId || "NA"
                    })`
                  : t("typography.na")}
              </Typography>
            </Grid>
          </>
        )}

        {rosterDetails.length > 0 ? (
          <Grid item xs={12}>
            <PrimeReactDataGrid
              dataKey="classRosterId"
              size="normal"
              removableSort
              resizableColumns
              showGridlines
              paginator
              rows={10}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate={t(
                "pagination_template.showing_first_to_last_of_totalrecords_record_s_",
              )}
              columnResizeMode="expand"
              value={rosterDetails}
              columns={requisitionDetailsColumns}
              filterprops={filterProps}
              selectionMode="multiple"
              dragSelection
              metaKeySelection={false}
              enableGlobalFilter={false}
              enableStickyHeader={true}
              enableScrollable={true}
              scrollHeight={`calc(100vh - ${requisitionDetails.length > 0 ? 510 : 350}px)`}
              sortMode="multiple"
              emptyMessage={t("empty_message.no_records_found_")}
            />
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "calc(100vh - 510px)",
                width: "100%",
                textAlign: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#3F5765",
                  fontWeight: "bold",
                  letterSpacing: "0.10em",
                  padding: 2,
                }}
              >
                {t("typography.no_requisition_roster_details_found_")}
              </Typography>
              {requisitionDetails.length === 0 && (
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "#3F5765",
                    letterSpacing: "0.05em",
                    padding: 1,
                  }}
                >
                  {t(
                    "typography.please_search_for_requesition_details_by_requisition_id",
                  )}
                </Typography>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
      <SnackBar snack={snack} snackReset={snackReset} />
    </>
  );
}
SearchRequisitions.propTypes = {
  setRosterLength: PropTypes.func,
};
